import { Marker } from '@react-google-maps/api';
import { WaypointType } from '@treadinc/horizon-api-spec';

import {
  isDefaultSiteUsages,
  type JobSiteUsage,
  type LoadSiteUsage,
  type OrderSiteUsage,
} from '~hooks/useSiteUsages/models';
import { createWaypointSvgDataUrl } from '~pages/Admin/Geo/mapIcons';

interface WaypointPoint {
  id: string;
  lat: number;
  lng: number;
  type: WaypointType | 'both';
  name: string;
  /** When true, renders the default-site (muted + dashed ring) treatment. */
  isDefaultSite?: boolean;
}

/**
 * Build distance-map waypoint points from site usages: one per site with valid
 * coordinates, collapsed to `'both'` for multi-type sites, flagged default when
 * every usage is a default, and repositioned to the live moving-site location.
 */
export function buildWaypointPoints(
  siteUsages: (JobSiteUsage | OrderSiteUsage | LoadSiteUsage)[],
  movingSiteInfo: { siteIndex: number } | null,
  movingSitePosition: { position: { lat: number; lng: number } } | null,
): WaypointPoint[] {
  return siteUsages
    .map((su, i): WaypointPoint | null => {
      if (!su.site.lat || !su.site.lon) return null;
      const types = su.usages.map((u) => u.waypointType);
      const type: WaypointType | 'both' =
        types.length > 1 ? 'both' : (types[0] ?? WaypointType.PICKUP);
      const isDefaultSite = isDefaultSiteUsages(su.usages);
      const onMovingSite = movingSiteInfo?.siteIndex === i && !!movingSitePosition;
      return {
        id: su.site.id,
        lat:
          onMovingSite && movingSitePosition
            ? movingSitePosition.position.lat
            : Number(su.site.lat),
        lng:
          onMovingSite && movingSitePosition
            ? movingSitePosition.position.lng
            : Number(su.site.lon),
        type,
        name: su.site.name,
        isDefaultSite,
      };
    })
    .filter((p): p is WaypointPoint => p !== null);
}

interface Props {
  points: WaypointPoint[];
}

export default function WaypointMarkers({ points }: Props) {
  return (
    <>
      {points.map((point) => (
        <Marker
          key={point.id}
          position={{ lat: point.lat, lng: point.lng }}
          icon={{
            url: createWaypointSvgDataUrl(point.type, point.isDefaultSite),
            anchor: new window.google.maps.Point(12, 12),
            scaledSize: new window.google.maps.Size(24, 24),
          }}
          title={point.name}
        />
      ))}
    </>
  );
}
